import { Component, inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';


import { NzButtonModule } from 'ng-zorro-antd/button';
import { TranslatePipe } from '@ngx-translate/core';
import { NZ_MODAL_DATA, NzModalModule, NzModalRef } from 'ng-zorro-antd/modal';
import { NzTreeSelectComponent } from 'ng-zorro-antd/tree-select';
import { ClassificationCodeService, RecommendationRecordsService } from '@beautyapp/core';
import { DatepickerComponent } from '../../../../../form/src/lib/datepicker/datepicker.component';
import { SelectComponent } from '../../../../../form/src/lib/select/select.component';

@Component({
  selector: 'beautyapp-create-recommendation',
  imports: [
    ReactiveFormsModule,
    DatepickerComponent,
    NzButtonModule,
    TranslatePipe,
    NzModalModule,
    NzTreeSelectComponent,
    SelectComponent
],
  templateUrl: './create-recommendation.component.html',
  styleUrl: './create-recommendation.component.css'
})
export class CreateRecommendationComponent implements OnInit {
  private fb = inject(FormBuilder);

  data = inject(NZ_MODAL_DATA);
  modal = inject(NzModalRef);
  recommendationRecordsService = inject(RecommendationRecordsService);
  classificationCodeService = inject(ClassificationCodeService);

  medicalSpecialties = this.classificationCodeService.getMedicalSpecialty();

  form: FormGroup;

  // Available recommendation types (can be extended as needed)
  recommendationTypes: any = [
    { label: 'ქირურგიული ჩარევები/პროცედურები', type: 'SURGICAL' },
    { label: 'ინსტრუმენტული გამოკვლევები', type: 'INSTRUMENTAL' },
    { label: 'კონსულტაციები ამბულატორიაში', type: 'CONSULTATION' },
    { label: 'რჩევა/დარიგება', type: 'OTHER' }
  ];

  // Field configuration per type
  fieldConfig: any = {
    SURGICAL: [
      { name: 'ncsp', label: 'NCSP', type: 'ncsp', required: false },
      { name: 'ncspComment', label: 'NCSP კომენტარი', type: 'textarea', required: false },
      { name: 'icd10', label: 'ICD10', type: 'icd', required: false },
      { name: 'icd10Comment', label: 'ICD10 კომენტარი', type: 'textarea', required: false }
    ],
    INSTRUMENTAL: [
      { name: 'ncsp', label: 'NCSP', type: 'ncsp', required: false },
      { name: 'instrumentalExplorationsDate', label: 'კვლევის თარიღი', type: 'date', required: false },
      { name: 'doctorComment', label: 'ექიმის კომენტარი', type: 'textarea', required: false }
    ],
    CONSULTATION: [
      { name: 'specialtyCode', label: 'Specialty Code', type: 'text', required: false },
      { name: 'deadline', label: 'Deadline', type: 'date', required: false }
    ],
    OTHER: [
      { name: 'recommendation', label: 'Recommendation', type: 'textarea', required: false }
    ]
  };

  get selectedType() {
    return this.form.get('type')?.value;
  }

  constructor() {
    this.form = this.fb.group({
      type: ['OTHER', Validators.required], // Dropdown for recommendation type
      icd10Id: [null],
      ncspId: [null],
      ncspComment: [''],
      icd10Comment: [''],
      instrumentalExplorationsDate: [''],
      doctorComment: [''],
      specialtyCode: [''],
      deadline: [''],
      recommendation: [''],
    });
  }

  ngOnInit(): void {

    if (this.data.data) {
      this.form.patchValue(this.data.data);
    }
  }

  // On type change, dynamically update form fields
  // onTypeChange(type: string): void {
  //
  //
  //   // Remove existing dynamic controls
  //   Object.keys(this.form.controls).forEach((control) => {
  //     if (control !== 'type') {
  //       this.form.removeControl(control);
  //     }
  //   });
  //
  //   console.log(this.form.value)
  //
  //   // Add dynamic controls based on selected type
  //   const fields = this.fieldConfig[type] || [];
  //   fields.forEach((field: any) => {
  //     this.form.addControl(
  //       field.name,
  //       this.fb.control('', field.required ? Validators.required : null)
  //     );
  //   });
  //   this.cdr.detectChanges()
  //
  //   this.selectedType = type;
  //   console.log(this.form.value)
  // }

  // Submit the form
  submit(): void {
    if (this.form.invalid) {
      return;
    }

    if (this.data.isTemplateMode) {
      const ncsp = this.findNcspNodeById(this.form.value?.ncspId, this.classificationCodeService.classificationNCSPCodes());
      const icd10 = this.findIcd10NodeById(this.form.value?.icd10Id, this.classificationCodeService.classificationICDCodes());

      console.log('NCSP:', ncsp);
      console.log('ICD10:', icd10);

      // In template mode, just return the form data without API call
      const recommendationData = {
        ...this.form.value,
        ncsp,
        icd10,
      };
      this.modal.close(recommendationData);
      return;
    } else {

      if (this.data.data) {
        this.recommendationRecordsService.update(this.data.data.id, {
          ...this.form.value,
          id: this.data.data.id,
          ehrId: this.data.ehrId,
        }).subscribe(res => {
          console.log(res);
          this.modal.triggerOk();
        });
      } else {
        console.log('Form submitted:', this.form.value);
        this.recommendationRecordsService.create({
          ...this.form.value,
          ehrId: this.data.ehrId
        }).subscribe(res => {
          console.log(res);
          this.modal.triggerOk();
        });
      }
    }
  }


  /**
   * Recursively search for NCSP node by ID in tree structure
   * @param ncspId - The ID to search for
   * @param nodes - The tree nodes to search in (defaults to all NCSP codes)
   * @returns The found node or null
   */
  findNcspNodeById(ncspId: string | null, nodes?: any[]): any | null {
    if (!ncspId) return null;

    const searchNodes = nodes || this.classificationCodeService.classificationNCSPCodes();

    for (const node of searchNodes) {
      // Check if current node matches
      if (node.key === ncspId || node.value === ncspId) {
        return node;
      }

      // Recursively search children
      if (node.children && node.children.length > 0) {
        const found = this.findNcspNodeById(ncspId, node.children);
        if (found) {
          return found;
        }
      }
    }

    return null;
  }

  /**
   * Recursively search for ICD10 node by ID in tree structure
   * @param icd10Id - The ID to search for
   * @param nodes - The tree nodes to search in (defaults to all ICD codes)
   * @returns The found node or null
   */
  findIcd10NodeById(icd10Id: string | null, nodes?: any[]): any | null {
    if (!icd10Id) return null;

    const searchNodes = nodes || this.classificationCodeService.classificationICDCodes();

    for (const node of searchNodes) {
      // Check if current node matches
      if (node.key === icd10Id || node.value === icd10Id) {
        return node;
      }

      // Recursively search children
      if (node.children && node.children.length > 0) {
        const found = this.findIcd10NodeById(icd10Id, node.children);
        if (found) {
          return found;
        }
      }
    }

    return null;
  }
}

